import React from "react";
import { Box, Button } from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import ShareIcon from "@mui/icons-material/Share";
import { useTranslation } from "react-i18next";

// Color verde corporativo de Tipify
const TIPIFY_GREEN = "#66bc5f";

/**
 * Panel de acciones rápidas para eventos
 * Muestra botones para: Llamar, WhatsApp, Cómo llegar, Compartir
 */
const QuickActionsPanel = ({ eventData, businessData }) => {
  const { t } = useTranslation();
  
  // Obtener WhatsApp: primero del evento, luego del business (si existe el campo), si no hay no mostrar
  const whatsapp = eventData?.whatsapp || businessData?.whatsapp || businessData?.telefonos?.[0] || null;
  
  // Obtener Instagram del business
  const instagramURL = businessData?.instagramURL || null;
  
  // Función para obtener el texto de un campo (maneja ObjectId, objetos y strings)
  const getTextValue = (value) => {
    if (!value) return null;
    // Si es un objeto con nombre o fullName, usar ese valor
    if (typeof value === 'object' && value.nombre) return value.nombre;
    if (typeof value === 'object' && value.fullName) return value.fullName;
    // Si es un objeto con _id (ObjectId), no usar
    if (typeof value === 'object' && value._id && !value.nombre && !value.fullName) return null;
    // Si es string, verificar si es un ObjectId (24 caracteres hexadecimales)
    if (typeof value === 'string') {
      // Si parece un ObjectId de MongoDB (24 caracteres hex), no usar
      if (/^[0-9a-fA-F]{24}$/.test(value)) return null;
      return value;
    }
    return null;
  };

  // Obtener ubicación: primero del evento, si no tiene usar la del espacio o del business
  const ubicacionEvento = eventData?.ubicacion;
  
  // Construir ubicación del espacio si existe y tiene datos
  let ubicacionEspacio = null;
  if (eventData?.espacio?.direccion) {
    const espacioDir = eventData.espacio.direccion;
    // Verificar si el espacio tiene al menos uno de los campos de dirección
    const espacioTieneDireccion = espacioDir.direccion || espacioDir.codigoPostal || espacioDir.poblacion ||
      (espacioDir.poblacion && typeof espacioDir.poblacion === 'object');
    
    if (espacioTieneDireccion) {
      ubicacionEspacio = {
        direccion: espacioDir.direccion,
        codigoPostal: espacioDir.codigoPostal,
        poblacion: espacioDir.poblacion, // Mantener como objeto para que getTextValue lo procese
        provincia: espacioDir.provincia,
        pais: espacioDir.pais,
        coordenadas: espacioDir.coordenadas || null
      };
    }
  }
  
  const ubicacionBusiness = businessData ? {
    direccion: businessData.direccion,
    codigoPostal: businessData.codigoPostal || businessData.codigo_postal,
    poblacion: businessData.localidad || businessData.poblacion, // Mantener estructura para procesamiento
    coordenadas: businessData.coordenadas || null
  } : null;
  
  // Usar ubicación del evento si tiene datos útiles (dirección o código postal), si no usar la del espacio, y finalmente la del business
  const tieneUbicacionEventoCompleta = ubicacionEvento && (
    (ubicacionEvento.direccion && ubicacionEvento.direccion.trim()) || 
    (ubicacionEvento.codigoPostal && ubicacionEvento.codigoPostal.trim())
  );
  
  // Si el evento tiene ubicación completa, usarla. Si solo tiene población, dar prioridad al espacio si está disponible
  const ubicacionRaw = tieneUbicacionEventoCompleta
    ? ubicacionEvento
    : (ubicacionEspacio || ubicacionEvento || ubicacionBusiness);
  
  // Limpiar la ubicación para evitar ObjectIds - usar getTextValue para todos los campos
  const ubicacion = ubicacionRaw ? {
    direccion: getTextValue(ubicacionRaw.direccion),
    codigoPostal: getTextValue(ubicacionRaw.codigoPostal),
    poblacion: getTextValue(ubicacionRaw.poblacion),
    coordenadas: ubicacionRaw.coordenadas || null
  } : null;

  const handleInstagram = () => {
    if (instagramURL) {
      let instagramUrl = instagramURL.trim();
      
      // Si viene con @, removerlo
      if (instagramUrl.startsWith("@")) {
        instagramUrl = instagramUrl.substring(1);
      }
      
      // Si no empieza con http, construir la URL completa
      if (!instagramUrl.startsWith("http")) {
        // Remover cualquier barra inicial o caracteres especiales
        instagramUrl = instagramUrl.replace(/^\/+/, "").replace(/[^a-zA-Z0-9_.]/g, "");
        instagramUrl = `https://www.instagram.com/${instagramUrl}/`;
      }
      
      window.open(instagramUrl, "_blank");
    }
  };

  const handleWhatsApp = () => {
    if (whatsapp) {
      const numero = whatsapp.replace(/[^0-9]/g, ""); // Eliminar caracteres no numéricos
      const mensaje = encodeURIComponent(
        `Hola, me gustaría obtener más información sobre el evento: ${eventData?.nombre || ""}`
      );
      window.open(`https://wa.me/${numero}?text=${mensaje}`, "_blank");
    }
  };

  const handleComoLlegar = () => {
    if (!ubicacion) return;
    
    // Construir dirección completa usando getTextValue para manejar ObjectIds poblados
    const partesDireccion = [];
    const direccion = getTextValue(ubicacion.direccion);
    const codigoPostal = getTextValue(ubicacion.codigoPostal);
    const poblacion = getTextValue(ubicacion.poblacion);
    
    // Orden: dirección, código postal, población
    if (direccion && direccion.trim()) partesDireccion.push(direccion.trim());
    if (codigoPostal && codigoPostal.trim()) partesDireccion.push(codigoPostal.trim());
    if (poblacion && poblacion.trim()) partesDireccion.push(poblacion.trim());
    
    const direccionCompleta = partesDireccion.join(", ");
    
    // Si hay coordenadas, usar coordenadas, si no usar dirección
    if (ubicacion.coordenadas?.lat && ubicacion.coordenadas?.lng) {
      window.open(
        `https://www.google.com/maps?q=${ubicacion.coordenadas.lat},${ubicacion.coordenadas.lng}`,
        "_blank"
      );
    } else if (direccionCompleta) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(direccionCompleta)}`,
        "_blank"
      );
    }
  };

  const handleCompartir = async () => {
    const url = window.location.href;
    const titulo = eventData?.nombre || businessData?.nombre_comercial || "Evento";
    const texto = eventData?.descripcion || businessData?.descripcion || "";

    if (navigator.share) {
      // Usar Web Share API si está disponible (móviles principalmente)
      try {
        await navigator.share({
          title: titulo,
          text: texto,
          url: url,
        });
      } catch (err) {
        // El usuario canceló o hubo un error
        console.log("Error al compartir:", err);
      }
    } else {
      // Fallback: copiar al portapapeles
      try {
        await navigator.clipboard.writeText(url);
        alert(t("event.actions.linkCopied"));
      } catch (err) {
        // Fallback final: mostrar URL
        prompt(t("event.actions.shareLink"), url);
      }
    }
  };

  // Si no hay ninguna acción disponible, no mostrar el panel
  if (!instagramURL && !whatsapp && !ubicacion) {
    return null;
  }

  // Contar botones disponibles para calcular el ancho
  const botonesDisponibles = [
    instagramURL && "instagram",
    whatsapp && "whatsapp",
    ubicacion && "llegar",
    "compartir" // Siempre disponible
  ].filter(Boolean);

  const numBotones = botonesDisponibles.length;
  const gap = "8px"; // Margen entre botones
  const anchoBoton = `calc((100% - (${numBotones - 1} * ${gap})) / ${numBotones})`;

  return (
    <Box
      sx={{
        px: "20px",
        mb: "80px",
        width: "100%",
        display: "grid",
        gap: "8px",
        gridTemplateColumns: {
          xs: "repeat(2, 1fr)", // 📱 mobile → 2 columnas
          sm: "repeat(4, 1fr)", // 💻 desktop → 4 columnas
        },
      }}
    >
      {instagramURL && (
        <Button
          variant="outlined"
          onClick={handleInstagram}
          sx={{
            flex: 1,
            textTransform: "none",
            fontSize: "1rem",
            padding: "10px 16px",
            backgroundColor: "#fff",
            color: "#E4405F", // Color oficial de Instagram
            borderColor: "#E4405F",
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            whiteSpace: "nowrap",
            borderRadius: "24px",
            "&:hover": {
              borderColor: "#E4405F",
              backgroundColor: "rgba(228, 64, 95, 0.05)",
            }
          }}
        >
          <InstagramIcon fontSize="small" />
          {t("success.instagram.cta")}
        </Button>
      )}

      {whatsapp && (
        <Button
          variant="outlined"
          onClick={handleWhatsApp}
          sx={{
            flex: 1,
            textTransform: "none",
            fontSize: "1rem",
            padding: "10px 16px",
            backgroundColor: "#fff",
            color: TIPIFY_GREEN,
            borderColor: TIPIFY_GREEN,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            whiteSpace: "nowrap",
            borderRadius: "24px",
            "&:hover": {
              borderColor: TIPIFY_GREEN,
              backgroundColor: "rgba(102, 188, 95, 0.05)",
            }
          }}
        >
          <WhatsAppIcon fontSize="small" />
          {t("event.actions.whatsapp")}
        </Button>
      )}

      {ubicacion && (
        <Button
          variant="outlined"
          onClick={handleComoLlegar}
          sx={{
            flex: 1,
            textTransform: "none",
            fontSize: "1rem",
            padding: "10px 16px",
            backgroundColor: "#fff",
            color: TIPIFY_GREEN,
            borderColor: TIPIFY_GREEN,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            whiteSpace: "nowrap",
            borderRadius: "24px",
            "&:hover": {
              borderColor: TIPIFY_GREEN,
              backgroundColor: "rgba(102, 188, 95, 0.05)",
            }
          }}
        >
          <FmdGoodOutlinedIcon fontSize="small" />
          {t("event.actions.directions")}
        </Button>
      )}

      <Button
        variant="outlined"
        onClick={handleCompartir}
        sx={{
          flex: 1,
          textTransform: "none",
          fontSize: "1rem",
          padding: "10px 16px",
          backgroundColor: "#fff",
          color: TIPIFY_GREEN,
          borderColor: TIPIFY_GREEN,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          whiteSpace: "nowrap",
            borderRadius: "24px",
          "&:hover": {
            borderColor: TIPIFY_GREEN,
            backgroundColor: "rgba(102, 188, 95, 0.05)",
          },
        }}
      >
        <ShareIcon fontSize="small" />
        {t("event.actions.share")}
      </Button>

    </Box>
  );
};

export default QuickActionsPanel;

